import { FormlyFieldConfig } from '@ngx-formly/core';
import { AgreementsFormOptions } from './agreements-form-options.interface';
import { toFormlyOptions } from '@lmc-app/applications/applications-form-helpers';

export function getCoAppAgreementFields(
  options: Partial<AgreementsFormOptions>,
  maxCalendarDate: string | Date,
  updateAgreement: Boolean,
  customFieldClass: string): FormlyFieldConfig[] {
    // DOB + Mobile on one row. Address block layout: Unit Number + Address on one row, City + Postal Code on the next, Province on its own row
    const colClass = (col: string) => customFieldClass.replace(/\bcol-12\b/, col);
    return [
        {
        className: 'col-12',
        expressions: {
          hide: (field: FormlyFieldConfig) => field.model?.HasCoApp?.value !== 'Yes',
        },
        fieldGroupClassName: 'row',
        ...(updateAgreement)
        ?
        {
            wrappers: ['expansion-panel'],
            props: {
                label: 'Details',
                groupHeading: 'Co-Applicant',
                icon: 'person',
                description: 'Name, contact & address',
                expanded: false,
                expClass: 'exp-class exp-group exp-group--first'
            },
        } : {},
        fieldGroup: [
        {
          key: 'CoAppFirstName.value',
          type: 'input',
          className: customFieldClass,
          props: {
            label: 'Firstname',
            appearance: 'outline',
            floatLabel: 'always',
          },
          expressions: {
            ...(updateAgreement) ?
            {
              'props.required': ''
            }
            :
            {
              hide: (field: FormlyFieldConfig) =>
              field.model?.CoAppFirstName?.isValid === true,
            },
          }
        },
        {
          key: 'CoAppMiddleName.value',
          type: 'input',
          className: customFieldClass,
          props: {
            label: 'Middlename',
            appearance: 'outline',
            floatLabel: 'always',
          },
          expressions: {
            ...(updateAgreement) ?
            {}
            :
            {
              hide: (field: FormlyFieldConfig) =>
              field.model?.CoAppMiddlename?.isValid === true,
            },
          }
        },
        {
          key: 'CoAppLastName.value',
          type: 'input',
          className: customFieldClass,
          props: {
            label: 'Lastname',
            appearance: 'outline',
            floatLabel: 'always',
          },
          expressions: {
            ...(updateAgreement) ?
            {
              'props.required': '',
            }
            :
            {
              hide: (field: FormlyFieldConfig) =>
                field.model?.CoAppLastName?.isValid === true,
              },
            }
        },
        {
          key: 'CoAppDOB.value',
          type: 'datepicker',
          className: colClass('col-12 col-md-6 pe-md-1'),
          props: {
            label: 'DOB',
            appearance: 'outline',
            floatLabel: 'always',
            datepickerOptions: {
              max: maxCalendarDate
            }
          },
          expressions: {
            ...(updateAgreement) ?
            {
              'props.required': '',
            }
            :
            {
              hide: (field: FormlyFieldConfig) =>
                field.model?.CoAppDOB?.isValid === true,
              'props.max': (field: FormlyFieldConfig) => {
                return maxCalendarDate;
              }
            }
          },
        },
        {
          key: 'CoAppMobile.value',
          type: 'input',
          className: colClass('col-12 col-md-6 ps-md-1'),
          props: {
            label: 'Mobile',
            appearance: 'outline',
            floatLabel: 'always',
          },
          expressions: {
            ...(updateAgreement) ?
            {
              'props.required': '',
            }
            :
            {
              hide: (field: FormlyFieldConfig) =>
                field.model?.CoAppMobile?.isValid === true,
            },
          }
        },
        {
          key: 'CoAppEmail.value',
          type: 'input',
          className: customFieldClass,
          props: {
            label: 'Email',
            appearance: 'outline',
            floatLabel: 'always',
          },
          expressions: {
            ...(updateAgreement) ?
            {
              'props.required': '',
            }
            :
            {
              hide: (field: FormlyFieldConfig) =>
                field.model?.CoAppEmail?.isValid === true,
              },
          }
        },
        {
          key: 'CoAppUnitNumber.value',
          type: 'input',
          className: colClass('col-12 col-md-4 pe-md-1'),
          props: {
            label: 'Unit Number',
            appearance: 'outline',
            floatLabel: 'always',
          },
          expressions: {
            ...(updateAgreement) ?
            {}
            :
            {
              hide: (field: FormlyFieldConfig) =>
                field.model?.CoAppAddress?.field_group === "OPTIONAL"
            },
          }
        },
        {
          key: 'CoAppAddress.value',
          type: 'input',
          className: colClass('col-12 col-md-8 ps-md-1'),
          props: {
            label: 'Address',
            appearance: 'outline',
            floatLabel: 'always',
          },
          expressions: {
            ...(updateAgreement) ?
            {
              'props.required': '',
            }
            :
            {
              hide: (field: FormlyFieldConfig) =>
                field.model?.CoAppAddress?.isValid === true,
            },
          }
        },
        {
          key: 'CoAppCity.value',
          type: 'input',
          className: colClass('col-12 col-md-6 pe-md-1'),
          props: {
            label: 'City',
            appearance: 'outline',
            floatLabel: 'always',
          },
          expressions: {
            ...(updateAgreement) ?
            {
              'props.required': '',
            }
            :
            {
              hide: (field: FormlyFieldConfig) =>
              field.model?.CoAppCity?.isValid === true,
            }
          },
        },
        {
          key: 'CoAppPostalCode.value',
          type: 'input',
          className: colClass('col-12 col-md-6 ps-md-1'),
          props: {
            label: 'Postal Code',
            appearance: 'outline',
            floatLabel: 'always',
          },
          expressions: {
            ...(updateAgreement) ?
            {
              'props.required': '',
            }
            :
            {
              hide: (field: FormlyFieldConfig) =>
                field.model?.CoAppPostalCode?.isValid === true,
            }
          },
        },
        {
          key: 'CoAppProvince.value',
          type: 'searchable-select',
          className: customFieldClass,
          props: {
            label: 'Province',
            appearance: 'outline',
            floatLabel: 'always',
            options: toFormlyOptions(options.provinces$),
            searchPlaceholder: 'Search provinces',
            noEntriesFoundLabel: 'No matching province found',
          },
          expressions: {
            ...(updateAgreement) ?
            {
              'props.required': '',
            }
            :
            {
              hide: (field: FormlyFieldConfig) =>
                field.model?.CoAppProvince?.isValid === true,
            }
          },
        },
      ]
    },
        {
        className: 'col-12',
        expressions: {
          hide: (field: FormlyFieldConfig) => field.model?.HasCoApp?.value !== 'Yes',
        },
        fieldGroupClassName: 'row',
        ...(updateAgreement)
        ?
        {
            wrappers: ['expansion-panel'],
            props: {
                label: 'Banking',
                icon: 'account_balance',
                description: 'Institution & account',
                expanded: false,
                expClass: 'exp-class exp-group exp-group--last'
            },
        } : {},
        fieldGroup: [
        {
          key: 'CoAppFinancialInstitutionName.value',
          type: 'input',
          className: customFieldClass,
          props: {
            label: 'Financial Institution Name',
            appearance: 'outline',
            floatLabel: 'always',
          },
          expressions: {
            ...(updateAgreement) ?
            {
              'props.required': '',
            }
            :
            {
              hide: (field: FormlyFieldConfig) =>
                field.model?.CoAppFinancialInstitutionName?.isValid === true,
            }
          },
        },
        {
          key: 'CoAppFinancialInstitutionNumber.value',
          type: 'input',
          className: customFieldClass,
          props: {
            label: 'Financial Institution Number',
            appearance: 'outline',
            floatLabel: 'always',
          },
          expressions: {
            ...(updateAgreement) ?
            {
              'props.required': '',
            }
            :
            {
              hide: (field: FormlyFieldConfig) =>
                field.model?.CoAppFinancialInstitutionNumber?.isValid === true,
            }
          },
        },
        {
          key: 'CoAppBankAccountNumber.value',
          type: 'input',
          className: customFieldClass,
          props: {
            label: 'Bank Account Number',
            appearance: 'outline',
            floatLabel: 'always',
          },
          expressions: {
            ...(updateAgreement) ?
            {
              'props.required': '',
            }
            :
            {
              hide: (field: FormlyFieldConfig) =>
                field.model?.CoAppBankAccountNumber?.isValid === true,
            }
          },
        },
        {
          key: 'CoAppBankTransitNo.value',
          type: 'input',
          className: customFieldClass,
          props: {
            label: 'Bank Transit No',
            appearance: 'outline',
            floatLabel: 'always',
          },
          expressions: {
            ...(updateAgreement) ?
            {
              'props.required': '',
            }
            :
            {
              hide: (field: FormlyFieldConfig) =>
                field.model?.CoAppBankTransitNo?.isValid === true,
            }
          },
        },
        {
          key: 'CoAppBranchAddress.value',
          type: 'input',
          className: customFieldClass,
          props: {
            label: 'Branch Address',
            appearance: 'outline',
            floatLabel: 'always',
          },
          expressions: {
            ...(updateAgreement) ?
            {
              'props.required': '',
            }
            :
            {
              hide: (field: FormlyFieldConfig) =>
                field.model?.CoAppBranchAddress?.isValid === true,
            }
          },
        },
        // // {
        // //   type: 'button',
        // //   props: {
        // //     text: 'Update',
        // //     onClick: (field: FormlyFieldConfig) => {
        // //         options?.onUpdate?.(field.model);
        // //     }
        // //   },
        // //   expressions: {
        // //           hide: () => !updateAgreement,
        // //           'props.disabled': (field: FormlyFieldConfig) => options?.hasChanges ? !options.hasChanges(field.model) : false,
        // //   }
        // // },
      ]
    },
  ]
}
